import { client } from "@/api/client";
import { validateResponse } from "@/api/utils";
import { z } from "zod";
import type { FilterOption } from "@/components/designSystem/FilterSelect";

const optionRowSchema = z.object({ id: z.number(), name: z.string() });
const optionListSchema = z.array(optionRowSchema);

const toOptions = (rows: { id: number; name: string }[]): FilterOption[] =>
  rows.map(({ id, name }) => ({ value: String(id), label: name }));

/** 검색은 호출측 Select 가 client-side 로 처리하므로 전량을 한 번만 받는다 */
const getOptions = async (
  endpoint: string,
  params?: Record<string, string>,
): Promise<FilterOption[]> => {
  const response = await client.get(endpoint, { params });
  return toOptions(
    validateResponse(
      optionListSchema,
      response.data.data,
      "GET",
      endpoint,
      params,
    ),
  );
};

/** 운영사 옵션. 비-MASTER 는 자사만 반환된다 */
export const getCpoOptions = () => getOptions("/cpo/options");

/** 고객사 옵션. 운영사에게는 자신과 계약된 고객사만 반환된다 */
export const getClientOptions = () => getOptions("/client/options");

/**
 * 사업자 옵션. 충전카드 소유·충전기 인증 허용처럼 **역할이 아니라 사업자**를 가리키는
 * 자리가 쓴다 — 운영사 자사 카드를 담으려면 고객사 목록으로는 부족하다.
 *
 * `service_type` 파라미터는 사라졌다(운영사·고객사 목록이 엔드포인트로 갈렸다).
 * 역할로 좁히는 것은 목록이 아니라 `owner_role_filter[]` 가 한다.
 */
export const getBusinessOptions = () => getOptions("/company/options");

/**
 * 고객사 역할을 가진 사업자 옵션.
 *
 * 값 축이 `company.id` 라 `card_company_ids` 필터와 짝이다 — `/client/options` 의 `id` 는
 * `client.id` 라 값 공간이 다른데 둘 다 숫자여서 잘못 써도 에러 없이 틀린 결과가 나온다.
 *
 * `/company/options` 는 MASTER 전용(403)이므로 MASTER 화면에서만 쓴다.
 */
export const getClientCompanyOptions = () =>
  getOptions("/company/options", { owner_role: "CLIENT" });

const clientOptionRowSchema = z.object({
  id: z.number(),
  company_id: z.number(),
  name: z.string(),
});

/**
 * 호출자 범위의 고객사 옵션. 값은 `id`(고객사) 가 아니라 **`company_id`(사업자)** 다 —
 * `card_company_ids` 가 사업자 축이라 그쪽에 맞춘다.
 *
 * 스코프는 서버가 건다: MASTER 전체 / 운영사는 활성 계약 고객사 / 고객사는 자신.
 */
export const getClientCompanyIdOptions = async (): Promise<FilterOption[]> => {
  const endpoint = "/client/options";
  const response = await client.get(endpoint);
  return validateResponse(
    z.array(clientOptionRowSchema),
    response.data.data,
    "GET",
    endpoint,
  ).map(({ company_id, name }) => ({ value: String(company_id), label: name }));
};

const entityTypeOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

/** 사업자 유형 옵션. context 에 따라 노출 범위가 다르므로 하드코딩하지 않는다 */
export const getEntityTypeOptions = async (
  context: "client" | "cpo",
): Promise<FilterOption[]> => {
  const params = { context };
  const response = await client.get("/company/entity-type-options", { params });
  return validateResponse(
    z.array(entityTypeOptionSchema),
    response.data.data,
    "GET",
    "/company/entity-type-options",
    params,
  );
};
